import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, Clock, Store, Sparkles } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const proxyImg = (id) => `${API_BASE}/jatak/public/image/${id}`;
const STORES = [["", "Alle"], ["Falster", "Falster"], ["Ringsted", "Ringsted"]];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
.jatak-root { font-family: 'IBM Plex Sans', system-ui, sans-serif; }
.jatak-display { font-family: 'Barlow Condensed', sans-serif; }
`;

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

export default function JatakPublic() {
  const [posts, setPosts] = useState([]);
  const [state, setState] = useState("loading");
  const [store, setStore] = useState("");

  useEffect(() => {
    let active = true;
    setState("loading");
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/jatak/public/posts?store=${encodeURIComponent(store)}`);
        if (!active) return;
        if (!r.ok) { setState("error"); return; }
        const d = await r.json();
        setPosts(d.posts || []);
        setState("ok");
      } catch { if (active) setState("error"); }
    })();
    return () => { active = false; };
  }, [store]);

  return (
    <div className="jatak-root min-h-screen bg-[#F8F9FA] pb-24" data-testid="jatak-public">
      <style>{CSS}</style>

      <header className="bg-gradient-to-br from-red-700 via-[#C8102E] to-rose-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14 relative">
          <Link to="/" className="inline-flex items-center gap-1.5 text-red-100 hover:text-white text-sm font-semibold mb-5 transition-colors" data-testid="jatak-back">
            <ArrowLeft className="w-4 h-4" /> Tilbage til tilbudsaviser
          </Link>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full pl-2.5 pr-3.5 py-1.5 mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="jatak-display font-bold uppercase tracking-wide text-sm">Lagersalg</span>
            </div>
            <h1 className="jatak-display text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.9] drop-shadow-sm">Ja tak tilbud</h1>
            <p className="text-red-100 text-sm sm:text-base mt-4 uppercase tracking-[0.22em] font-semibold">Ugens Ja tak-varer fra vores butikker</p>
          </div>
        </div>
        <div className="h-3 bg-yellow-300" />
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-8">
        {/* Butik-filter */}
        <div className="flex items-center justify-center gap-2 mb-8 flex-wrap" data-testid="jatak-store-filter">
          {STORES.map(([val, label]) => (
            <button
              key={val || "all"}
              onClick={() => setStore(val)}
              data-testid={`jatak-store-${val || "all"}`}
              className={`jatak-display text-base font-bold uppercase tracking-wide rounded-full px-5 py-2 transition-all ${store === val ? "bg-[#C8102E] text-white shadow-md" : "bg-white text-zinc-500 border border-zinc-200 hover:border-[#C8102E] hover:text-[#C8102E]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {state === "loading" && (
          <div className="flex items-center justify-center py-24" data-testid="jatak-loading">
            <Loader2 className="w-7 h-7 animate-spin text-[#D90429]" />
          </div>
        )}

        {state === "error" && (
          <p className="text-center text-zinc-400 py-24" data-testid="jatak-error">Kunne ikke hente Ja tak-opslagene lige nu.</p>
        )}

        {state === "ok" && posts.length === 0 && (
          <div className="text-center py-24" data-testid="jatak-empty">
            <Sparkles className="w-12 h-12 text-red-200 mx-auto mb-4" />
            <p className="jatak-display text-2xl font-extrabold uppercase tracking-tight text-zinc-800">Ingen Ja tak-tilbud lige nu</p>
            <p className="text-zinc-500 text-sm mt-2">Kig forbi igen snart — nye Ja tak-varer er på vej.</p>
          </div>
        )}

        {state === "ok" && posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jatak-grid">
            {posts.map((p) => (
              <article key={p.id} className="bg-white rounded-2xl border border-zinc-200/90 shadow-sm overflow-hidden flex flex-col" data-testid={`jatak-post-${p.id}`}>
                {p.image && (
                  <div className="relative w-full aspect-square bg-zinc-50 overflow-hidden">
                    <img src={proxyImg(p.id)} alt="Ja tak tilbud" className="w-full h-full object-cover" loading="lazy" />
                    {p.store && (
                      <span className="absolute top-3 left-3 jatak-display inline-flex items-center gap-1 bg-[#C8102E] text-white font-bold uppercase text-xs tracking-wide px-2.5 py-1 rounded-md shadow-sm">
                        <Store className="w-3 h-3" />{p.store}
                      </span>
                    )}
                  </div>
                )}
                <div className="p-4 flex flex-col flex-grow">
                  {p.jatak_times && p.jatak_times.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2.5" data-testid={`jatak-times-${p.id}`}>
                      {p.jatak_times.map((t, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-yellow-300 text-zinc-900 font-bold text-xs rounded-full px-2.5 py-1">
                          <Clock className="w-3 h-3" /> Kl. {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed flex-grow">{p.message}</p>
                  {p.created_time && <p className="text-xs text-zinc-400 mt-3">{fmtDate(p.created_time)}</p>}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
